import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Avatar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Wallet,
  ShoppingBag,
  Users2,
  TrendingUp,
  Plus,
  Globe,
  Building2,
  ShoppingCart,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Sidebar } from './Sidebar';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { salesService } from '../services/salesService';
import { ProductForm } from './ProductForm';
import { Outlet } from 'react-router-dom';

const Dashboard = () => {
  const [openAddProduct, setOpenAddProduct] = React.useState(false);
  const [salesData, setSalesData] = useState<any[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState('');
  const [totalSales, setTotalSales] = useState(0);
  const [numberOfSales, setNumberOfSales] = useState(0);
  const { language, setLanguage } = useLanguage();
  const { user } = useAuth();

  const handleOpenDialog = () => setOpenAddProduct(true);
  const handleCloseDialog = () => setOpenAddProduct(false);
  const handleSuccess = () => {
    // Vous pouvez ajouter ici une logique pour rafraîchir les données si nécessaire
    handleCloseDialog();
  };

  const text = {
    fr: {
      dashboard: 'Tableau de bord',
      welcome: 'Bienvenue sur votre espace de gestion',
      companyName: 'Entreprise',
      totalSales: 'Total des Ventes',
      numberOfSales: 'Nombre de Ventes',
      activeClients: 'Clients Actifs',
      growth: 'Croissance',
      recentSales: 'Dernières Ventes',
      salesChart: 'Évolution des Ventes',
      date: 'Date',
      client: 'Client',
      amount: 'Montant',
      status: 'Statut',
      topClients: 'Meilleurs Clients',
      lastUpdate: 'Dernière mise à jour',
      addProduct: 'Ajouter un produit',
      completed: 'Complété',
      pending: 'En attente',
      productCode: 'Code du produit',
      productName: 'Nom du produit',
      description: 'Description',
      purchasePrice: 'Prix d\'achat',
      salePrice: 'Prix de vente',
      quantity: 'Quantité',
      cancel: 'Annuler',
      save: 'Enregistrer',
      saving: 'Enregistrement en cours',
      totalPurchases: 'Total des Achats',
      numberOfUsers: 'Utilisateurs',
    },
    en: {
      dashboard: 'Dashboard',
      welcome: 'Welcome to your management space',
      companyName: 'Company',
      totalSales: 'Total Sales',
      numberOfSales: 'Number of Sales',
      activeClients: 'Active Clients',
      growth: 'Growth',
      recentSales: 'Recent Sales',
      salesChart: 'Sales Evolution',
      date: 'Date',
      client: 'Client',
      amount: 'Amount',
      status: 'Status',
      topClients: 'Top Clients',
      lastUpdate: 'Last update',
      addProduct: 'Add product',
      completed: 'Completed',
      pending: 'Pending',
      productCode: 'Product Code',
      productName: 'Product Name',
      description: 'Description',
      purchasePrice: 'Purchase Price',
      salePrice: 'Sale Price',
      quantity: 'Quantity',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving',
      totalPurchases: 'Total Purchases',
      numberOfUsers: 'Users',
    }
  };

  const currentText = text[language];

  // Données d'exemple - À remplacer par les données réelles de votre backend
  const stats = {
    totalVentes: '120,000 FC',
    nombreVentes: '45',
    clientsActifs: '28',
    croissance: '+15%',
  };

  const salesDataExample = [
    { date: '2024-01-15', ventes: 4000 },
    { date: '2024-01-16', ventes: 3000 },
    { date: '2024-01-17', ventes: 5000 },
    { date: '2024-01-18', ventes: 2780 },
    { date: '2024-01-19', ventes: 1890 },
    { date: '2024-01-20', ventes: 2390 },
    { date: '2024-01-21', ventes: 3490 },
  ];

  const recentSales = [
    { id: 1, date: '2024-01-21', client: 'Jean Dupont', montant: '15,000 FC', statut: 'Complété' },
    { id: 2, date: '2024-01-21', client: 'Marie Claire', montant: '8,500 FC', statut: 'En attente' },
    { id: 3, date: '2024-01-20', client: 'Pierre Martin', montant: '12,000 FC', statut: 'Complété' },
    { id: 4, date: '2024-01-20', client: 'Sophie Laurent', montant: '9,000 FC', statut: 'Complété' },
  ];

  useEffect(() => {
    const fetchSales = async () => {
      if (user?.companyId) {
        setSalesLoading(true);
        try {
          console.log('Fetching sales for company:', user?.companyId);
          const data = await salesService.getSales(user?.companyId);
          console.log('Sales data received:', data);
          setSalesData(data);
          
          // Calculer le total des ventes
          const total = data.reduce((sum, sale) => sum + (sale.totalAmount || 0), 0);
          setTotalSales(total);
          
          // Définir le nombre total de ventes
          setNumberOfSales(data.length);
        } catch (error) {
          console.error('Erreur lors de la récupération des ventes:', error);
          setSalesError('Erreur lors de la récupération des ventes');
        } finally {
          setSalesLoading(false);
        }
      } else {
        console.log('No company ID found in user object:', user);
      }
    };

    fetchSales();
  }, [user?.companyId]);

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          width: { xs: '100%', sm: `calc(100% - 240px)` },
          ml: { xs: 0, sm: `240px` },
          transition: 'margin 0.2s ease-in-out',
          overflow: 'hidden'
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Dashboard;